import { memo, useState, useRef, useCallback } from "react";
import { Image, X, Loader2, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

interface ImagePickerProps {
  chatId: string;
  onImageSent: () => void;
}

const ImagePicker = memo(({ chatId, onImageSent }: ImagePickerProps) => {
  const { user } = useAuthContext();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  }, []);

  const handleCancel = useCallback(() => {
    setSelectedImage(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [previewUrl]);

  const handleSend = useCallback(async () => {
    if (!selectedImage || !user) return;

    setIsSending(true);
    try {
      // Upload image to storage
      const fileName = `${user.id}/${Date.now()}-${selectedImage.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("chat-images")
        .upload(fileName, selectedImage, {
          cacheControl: "3600",
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("chat-images")
        .getPublicUrl(uploadData.path);

      // Send message with image URL
      const { error: messageError } = await supabase
        .from("messages")
        .insert({
          chat_id: chatId,
          sender_id: user.id,
          content: "📷 Photo",
          image_url: urlData.publicUrl,
        });

      if (messageError) throw messageError;

      handleCancel();
      onImageSent();
    } catch (error) {
      console.error("Failed to send image:", error);
    } finally {
      setIsSending(false);
    }
  }, [selectedImage, user, chatId, onImageSent, handleCancel]);

  // Show preview modal if image selected
  if (selectedImage && previewUrl) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4">
        <div className="relative max-w-md w-full">
          <button
            onClick={handleCancel}
            className="absolute -top-12 right-0 p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <img
            src={previewUrl}
            alt="Preview"
            className="w-full rounded-xl shadow-2xl"
          />
          
          <button
            onClick={handleSend}
            disabled={isSending}
            className="absolute -bottom-16 left-1/2 -translate-x-1/2 flex items-center gap-2 px-6 py-3 rounded-full pop-gradient text-white disabled:opacity-50"
          >
            {isSending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Send</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="p-2 rounded-full hover:bg-muted transition-colors"
        title="Send image"
      >
        <Image className="w-6 h-6" />
      </button>
    </>
  );
});

ImagePicker.displayName = "ImagePicker";

export default ImagePicker;
